import { useState } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import { useAuthenticationDetails } from 'hooks/useAuthenticationDetails'
import MultiInventoryLocationsPicker from '../sharedComponents/MultiInventoryLocationsPicker'
import {
	downloadInventoryAdjustmentsPdf,
	getFilename,
	Reason,
	SelectedInventoryLocation
} from '../helpers/downloadHelpers'
import { useReasons } from 'views/reports/views/InventoryAdjustments/hooks/useReasonFilter'
import useDateRangeFilter from 'views/reports/views/InventoryAdjustments/hooks/useDateRangeFilter'
import {
	EuiButton,
	EuiCheckbox,
	EuiFlexGrid,
	EuiFlexItem,
	EuiFormRow,
	EuiHorizontalRule,
	EuiModalBody,
	EuiModalFooter,
} from '@equipmentshare/ds2'
import useShowError from 'hooks/useShowError'
import { gql, useLazyQuery } from '@apollo/client'
import { Moment } from 'moment'

export type ExportAdjustmentsCsvModalProps = {
	onCancel: () => void,
	onSuccess: () => void,
}

const formDataIsValid = (
	form: Form,
): boolean => !!form.createdDateStart && !!form.createdDateEnd && !!form.inventoryLocations.length && !!form.reasons.length

export type QueryReturnType = { manualAdjustments: { edges?: { node: { id: number } }[] } };
const QUERY = gql`query AtLeastOneAdjustment($reasons: [ManualAdjustmentReasonType!], $inventoryLocations: [ID!], $createdDateStart: DateTime, $createdDateEnd: DateTime, $paging: CursorPaging) {
	manualAdjustments (reasons: $reasons, inventoryLocations: $inventoryLocations, createdDateStart: $createdDateStart, createdDateEnd: $createdDateEnd, paging: $paging) {
  	edges {
    	node {
				id
			}
		}
  }
}`


type Form = {
  reasons: Reason[],
  inventoryLocations: SelectedInventoryLocation[],
  createdDateStart?: Moment,
  createdDateEnd?: Moment,
}

export default function ExportAdjustmentsCsvModal( { onCancel, onSuccess }: ExportAdjustmentsCsvModalProps ) {
	const showError = useShowError()
	const [ isLoading, setIsLoading ] = useState( false )
	const [ canDownload, setCanDownload ] = useState<boolean>( false )
	const [ form, setForm ] = useState<Form>( {
		reasons: [],
		inventoryLocations: [],
		createdDateStart: undefined,
		createdDateEnd: undefined,
	} )
	const [ checkForAdjustmentsData, { loading: fetchingData } ] = useLazyQuery<QueryReturnType>( QUERY, { variables: {
		paging: {
			first: 1
		}
	} } )

	const handleFormChange = async( f: Partial<Form> ) => {
		const newFormState = { ...form, ...f }
		setForm( newFormState )
		if ( formDataIsValid( newFormState ) ) {
			const result = await checkForAdjustmentsData(
				{
					variables: {
						reasons: newFormState.reasons.map( ( r ) => r.value ),
						inventoryLocations: newFormState.inventoryLocations.map( ( l ) => l.id ),
						createdDateStart: newFormState.createdDateStart,
						createdDateEnd: newFormState.createdDateEnd
					}
				}
			)
			if ( result.error ) {
				showError(
					'ExportAdjustmentsCsvModal',
					'We are experiencing technical difficulties fetching manual adjustment data. Please contact Support'
				)
				setCanDownload( false )
			} else if ( ( result.data?.manualAdjustments.edges?.length ?? 0 ) < 1 ) {
				showError( 'ExportAdjustmentsCsvModal', 'No data available based on your selections' )
				setCanDownload( false )
			} else {
				setCanDownload( true )
			}
		} else {
			setCanDownload( false )
		}
	}

	const [ selectedInventoryLocationIds, setSelectedInventoryLocationIds ] = useState<string[]>( [] )
	const { accessToken } = useAuthenticationDetails()
	const { render: renderDateRangeFilter, filterVariables: dateRangeFilterVariables } = useDateRangeFilter(
		{
			onStartDateChange: async( date?: Moment ) => {
				await handleFormChange( { createdDateStart: date } )
			},
			onEndDateChange: async( date?: Moment ) => {
				await handleFormChange( { createdDateEnd: date } )
			}
		}
	)
	const reasons = useReasons()

	const handleReasonChange = ( reason: Reason ) => async() => {
		const reasonFound = form.reasons.find( ( r ) => r.label === reason.label )
		if ( reasonFound ) {
			await handleFormChange( { reasons: form.reasons.filter( ( r ) => r.label !== reason.label ) } )
		} else {
			await handleFormChange( { reasons: [ ...form.reasons, reason ] } )
		}
	}

	const onDownloadClick = async() => {
		heapEvent( 'Inventory - Export Adjustment CSV Modal - Click - Download', { selected_store_ids: selectedInventoryLocationIds?.join( ',' ) } )
		setIsLoading( true )
		try {
			await downloadInventoryAdjustmentsPdf(
				accessToken,
				selectedInventoryLocationIds,
				form.reasons,
				dateRangeFilterVariables?.createdDateStart,
				dateRangeFilterVariables?.createdDateEnd,
				getFilename( form.inventoryLocations, true ),
				true
			)
			setIsLoading( false )
			onSuccess()
		} catch ( e: any ) {
			setIsLoading( false )
			showError( 'ExportAdjustmentsCsvModal', e?.message ?? 'Sorry, an error occurred' )
		}
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Export Adjustment CSV Modal - Click - Cancel', {} )
		onCancel()
	}

	const isRadioDisabled = selectedInventoryLocationIds.length < 2

	return (
		<>
			<EuiModalBody>
				<EuiFormRow label="Date Range *">
					{renderDateRangeFilter()}
				</EuiFormRow>
				<EuiHorizontalRule />
				<EuiFormRow label="Select Branch *">
					<MultiInventoryLocationsPicker
						selectedIds={selectedInventoryLocationIds}
						setSelectedIds={setSelectedInventoryLocationIds}
						setSelectedInventoryLocations={async( selectedInventoryLocations ) => {
							await handleFormChange( { inventoryLocations: selectedInventoryLocations } )
						}}
						height={'25vh'}
					/>
				</EuiFormRow>
				<EuiHorizontalRule />
				<EuiFormRow label="Include Reason *" isDisabled={isRadioDisabled}>
					<EuiFlexGrid gutterSize="none" direction={'row'} columns={2}>
						{
							reasons?.map( ( reason ) => (
								<EuiFlexItem key={reason.value}>
									<EuiCheckbox
										id={reason.value}
										label={reason.label}
										name={reason.label}
										checked={form.reasons.find( ( r ) => r.label === reason.label ) !== undefined}
										onChange={handleReasonChange( reason )}
									/>
								</EuiFlexItem>
							) )
						}
					</EuiFlexGrid>
				</EuiFormRow>
				<EuiHorizontalRule />
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					color="text"
					onClick={handleCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					fill
					isLoading={isLoading || fetchingData}
					isDisabled={!canDownload}
					onClick={onDownloadClick}
					id="downloadBtn"
				>
					Download
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
